
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { TooltipProvider, Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Swords, Users, Trophy, BarChart2, MoreHorizontal, PlayCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useIsMobile, useIsTablet } from '@/hooks/use-mobile';
import { motion } from 'framer-motion';
import { ROUTES } from '@/constants/routes';
import { useFeatureTracking } from '@/hooks/useFeatureTracking';

interface QuickActionsBarProps {
  onCreatePoll: () => void;
  onFindPlayers: () => void;
  onCreateTournament: () => void;
}

interface Action {
  id: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick: () => void;
  color: string;
}

const ActionButton: React.FC<{ action: Action; isMobile: boolean; isTablet: boolean; index: number }> = ({ 
  action, 
  isMobile, 
  isTablet,
  index 
}) => {
  const buttonContent = (
    <>
      <Button
        onClick={action.onClick}
        className={`${action.color} text-white ${
          isMobile 
            ? 'h-14 w-14 rounded-full p-0 flex items-center justify-center' 
            : isTablet
            ? 'h-11 px-5 flex items-center gap-2 rounded-md'
            : 'h-12 px-6 flex items-center gap-3 rounded-lg'
        } shadow-sm transition-all duration-200 hover:shadow-md`}
        aria-label={action.label}
        title={action.label}
      >
        <action.icon className={isMobile ? "h-6 w-6" : "h-4 w-4"} />
        {!isMobile && <span className="text-sm">{action.label}</span>}
      </Button>
      {isMobile && (
        <span className="text-xs text-muted-foreground mt-1 text-center leading-tight max-w-[70px]">
          {action.label}
        </span>
      )}
    </>
  );

  const animationProps = {
    initial: { opacity: 0, y: isMobile ? 20 : 0, x: isMobile ? 0 : 20 },
    animate: { opacity: 1, y: 0, x: 0 },
    transition: { delay: index * 0.1, duration: 0.3 }
  };

  if (isMobile) {
    return (
      <motion.div
        {...animationProps}
        className="flex flex-col items-center flex-1 cursor-pointer"
        onClick={action.onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && action.onClick()}
        aria-label={action.label}
      >
        <Button
          className={`${action.color} text-white h-14 w-14 rounded-full p-0 flex items-center justify-center shadow-sm transition-all duration-200 hover:shadow-md pointer-events-none`}
          aria-hidden="true"
          tabIndex={-1}
        >
          <action.icon className="h-6 w-6" />
        </Button>
        <span className="text-xs text-muted-foreground mt-1 text-center leading-tight max-w-[70px]">
          {action.label}
        </span>
      </motion.div>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <motion.div {...animationProps}>
          <div className="flex flex-col items-center">
            {buttonContent}
          </div>
        </motion.div>
      </TooltipTrigger>
      <TooltipContent>
        <p>{action.label}</p>
      </TooltipContent>
    </Tooltip>
  );
};

interface MoreSheetItem {
  id: string;
  icon: React.ComponentType<{ className?: string }>;
  iconBg: string;
  label: string;
  description: string;
  onClick: () => void;
}

const MoreSheetRow: React.FC<{ item: MoreSheetItem; onClose: () => void }> = ({ item, onClose }) => (
  <button
    onClick={() => { item.onClick(); onClose(); }}
    className="flex items-center gap-4 w-full p-4 rounded-xl hover:bg-muted transition-colors text-left"
  >
    <div className={`${item.iconBg} h-12 w-12 rounded-full flex items-center justify-center flex-shrink-0`}>
      <item.icon className="h-6 w-6 text-white" />
    </div>
    <div className="flex flex-col">
      <span className="font-semibold text-foreground text-sm">{item.label}</span>
      <span className="text-xs text-muted-foreground mt-0.5">{item.description}</span>
    </div>
  </button>
);

export const QuickActionsBar: React.FC<QuickActionsBarProps> = ({
  onCreatePoll,
  onFindPlayers,
  onCreateTournament,
}) => {
  const navigate = useNavigate();
  const { user, ready } = useAuth();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const { t } = useTranslation();
  const { trackButtonClick, trackMatchCreation, trackPollInteraction, trackTournamentInteraction } = useFeatureTracking();
  const [moreOpen, setMoreOpen] = useState(false);

  // Primary actions always visible on mobile (first 3) + More button
  const primaryActions: Action[] = useMemo(() => [
    {
      id: 'new-match',
      icon: Swords,
      label: t('quickActions.newMatch'),
      onClick: () => {
        trackButtonClick('New Match', 'quick_actions');
        trackMatchCreation('quick_action_clicked', { source: 'home_page' });
        navigate(ROUTES.CREATE_MATCH);
      },
      color: 'bg-padel-green-light hover:bg-padel-green-dark'
    },
    {
      id: 'create-poll',
      icon: BarChart2,
      label: t('quickActions.createPoll'),
      onClick: () => {
        trackButtonClick('Create Poll', 'quick_actions');
        trackPollInteraction('create_poll_clicked', { source: 'home_page' });
        onCreatePoll();
      },
      color: 'bg-padel-blue-light hover:bg-padel-blue-dark'
    },
    {
      id: 'find-players',
      icon: Users,
      label: t('quickActions.findPlayers'),
      onClick: () => {
        trackButtonClick('Find Players', 'quick_actions');
        trackButtonClick('Find Players Action', 'player_matching', { source: 'home_page' });
        onFindPlayers();
      },
      color: 'bg-purple-500 hover:bg-purple-600'
    },
  ], [navigate, onCreatePoll, onFindPlayers, trackButtonClick, trackMatchCreation, trackPollInteraction, t]);

  // Additional actions shown on desktop/tablet inline, on mobile inside the More sheet
  const extendedActions: Action[] = useMemo(() => [
    {
      id: 'create-tournament',
      icon: Trophy,
      label: t('quickActions.createTournament'),
      onClick: () => {
        trackButtonClick('Create Tournament', 'quick_actions');
        trackTournamentInteraction('create_tournament_clicked', { source: 'home_page' });
        onCreateTournament();
      },
      color: 'bg-yellow-500 hover:bg-yellow-600'
    },
    {
      id: 'feed',
      icon: PlayCircle,
      label: t('quickActions.feed'),
      onClick: () => {
        trackButtonClick('Feed', 'quick_actions');
        navigate(ROUTES.FEED);
      },
      color: 'bg-padel-green-light hover:bg-padel-green-dark'
    },
  ], [navigate, onCreateTournament, trackButtonClick, trackTournamentInteraction, t]);

  const moreSheetItems: MoreSheetItem[] = useMemo(() => [
    {
      id: 'feed',
      icon: PlayCircle,
      iconBg: 'bg-padel-green-light',
      label: t('quickActions.feed'),
      description: t('feed.title') ? 'Browse community activity' : 'Browse community activity',
      onClick: () => {
        trackButtonClick('Feed', 'more_sheet');
        navigate(ROUTES.FEED);
      }
    },
    {
      id: 'create-tournament',
      icon: Trophy,
      iconBg: 'bg-yellow-500',
      label: t('quickActions.createTournament'),
      description: 'Start a new tournament',
      onClick: () => {
        trackButtonClick('Create Tournament', 'more_sheet');
        trackTournamentInteraction('create_tournament_clicked', { source: 'home_page_more' });
        onCreateTournament();
      }
    },
  ], [navigate, onCreateTournament, trackButtonClick, trackTournamentInteraction, t]);

  if (!user || !ready) return null;

  const allDesktopActions = [...primaryActions, ...extendedActions];

  return (
    <div className="px-4 mb-6">
      <TooltipProvider>
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          className={`flex ${
            isMobile 
              ? 'justify-between items-start' 
              : isTablet
              ? 'gap-4 justify-center'
              : 'gap-6 justify-center max-w-4xl mx-auto'
          } pb-2 scrollbar-hide`}
        >
          {isMobile ? (
            <>
              {primaryActions.map((action, index) => (
                <ActionButton 
                  key={action.id}
                  action={action} 
                  isMobile={true} 
                  isTablet={false}
                  index={index} 
                />
              ))}

              {/* More button */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: primaryActions.length * 0.1, duration: 0.3 }}
                className="flex flex-col items-center flex-1 cursor-pointer"
                onClick={() => setMoreOpen(true)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && setMoreOpen(true)}
                aria-label={t('quickActions.more')}
              >
                <Button
                  className="bg-muted hover:bg-muted/80 text-muted-foreground h-14 w-14 rounded-full p-0 flex items-center justify-center shadow-sm transition-all duration-200 hover:shadow-md pointer-events-none"
                  aria-hidden="true"
                  tabIndex={-1}
                >
                  <MoreHorizontal className="h-6 w-6" />
                </Button>
                <span className="text-xs text-muted-foreground mt-1 text-center leading-tight max-w-[70px]">
                  {t('quickActions.more')}
                </span>
              </motion.div>
            </>
          ) : (
            allDesktopActions.map((action, index) => (
              <ActionButton 
                key={action.id}
                action={action} 
                isMobile={false} 
                isTablet={isTablet}
                index={index} 
              />
            ))
          )}
        </motion.div>
      </TooltipProvider>

      {/* More Sheet (mobile only) */}
      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl pb-safe-nav">
          <SheetHeader className="mb-4">
            <div className="mx-auto w-10 h-1 rounded-full bg-muted-foreground/30 mb-3" />
            <SheetTitle className="text-left">{t('quickActions.more')}</SheetTitle>
          </SheetHeader>
          <div className="flex flex-col gap-1">
            {moreSheetItems.map((item) => (
              <MoreSheetRow key={item.id} item={item} onClose={() => setMoreOpen(false)} />
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};
